import React, { useState } from "react";
import { Box, Typography, Button, TextField } from "@mui/material";

interface CompanyNameEditProps {
  companyName: string;
  onSave: (newName: string) => Promise<void>;
}

export const CompanyNameEdit: React.FC<CompanyNameEditProps> = ({
  companyName,
  onSave,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleEdit = () => {
    setEditedName(companyName);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedName("");
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!editedName.trim()) return;

    setIsSaving(true);
    try {
      await onSave(editedName.trim());
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update company name:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ mb: 5 }}>
      <Typography
        variant="overline"
        sx={{
          fontWeight: 600,
          color: "text.secondary",
          fontSize: 12,
          letterSpacing: 0.5,
          mb: 1,
          display: "block",
        }}
      >
        Company Name
      </Typography>

      {isEditing ? (
        <Box>
          <TextField
            fullWidth
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            variant="outlined"
            autoFocus
            disabled={isSaving}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving || !editedName.trim()}
              sx={{
                bgcolor: "grey.900",
                color: "white",
                px: 3,
                py: 1,
                fontWeight: 600,
                "&:hover": { bgcolor: "grey.800" },
              }}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSaving}
              sx={{ px: 3, py: 1, fontWeight: 600 }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: 28 }}>
            {companyName}
          </Typography>
          <Button
            variant="contained"
            onClick={handleEdit}
            sx={{
              bgcolor: "grey.900",
              color: "white",
              px: 3,
              py: 1,
              fontWeight: 600,
              "&:hover": { bgcolor: "grey.800" },
            }}
          >
            Edit
          </Button>
        </Box>
      )}
    </Box>
  );
};
